import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { catchError, debounceTime, Observable, switchMap, throwError } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { IrisMsalService } from '@iris/modules/msal/msal.service';
import { IrisUserService } from '@iris/common/services/user.service';
import { IrisXHeadersEnum } from '@iris/common/models/IrisXHeadersEnum';

const EMAIL_CLIENT_URL_PART = '/mail-client/';
const ONE_DRIVE_CONNECTOR_URL_PART = '/one-drive-connector/';

const REQUIRED_AZURE_TOKEN = [
  EMAIL_CLIENT_URL_PART,
  ONE_DRIVE_CONNECTOR_URL_PART,
];

@Injectable()
export class IrisAzureAuthInterceptor implements HttpInterceptor {
  private readonly msalService = inject(IrisMsalService);
  private readonly userService = inject(IrisUserService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const tokenRequired = REQUIRED_AZURE_TOKEN.some(urlPart => req.url.includes(urlPart));
    if (!tokenRequired || !this.userService.me.ssoActive) {
      return next.handle(req);
    }
    return this.addTokenToRequest(req, next).pipe(
      catchError((error: HttpErrorResponse) => {
        const serverError = error.error as Record<string, unknown>;
        const errorStatus = error.status as HttpStatusCode;
        const serverErrorStatus = serverError.status as HttpStatusCode;
        if (errorStatus === HttpStatusCode.BadRequest && serverErrorStatus === HttpStatusCode.Unauthorized) {
          return this.msalService.acquireTokenSilent({ forceRefresh: true }).pipe(
            switchMap(() => this.addTokenToRequest(req, next)),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private addTokenToRequest(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.msalService.azureAccessToken$.pipe(
      debounceTime(0),
      switchMap(accessToken => next.handle(req.clone({ headers: req.headers.set(IrisXHeadersEnum.AZURE_TOKEN, accessToken) }))),
    );
  }
}
